import { Button, useEffectOnValueChange } from '@timelyapp/react';
import classNames from 'classnames';
import DropdownList from 'components/DropdownList';
import dropdownStyles from 'components/DropdownList.module.scss';
import DropdownListItem from 'components/DropdownListItem';
import { Cog, DownDoubleChevron } from 'components/Icons';
import Popover from 'components/popovers/Popover';
import { WidgetDragHandleIcon } from 'components/superreports/WidgetIcons';
import React, { useCallback, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import type { WidgetEntity, WidgetWithData } from 'redux/modules/superreports.types';
import { getRowIdsFromWidget } from 'utils/superreports';
import styles from './WidgetHeader.module.scss';

interface Props {
  options?: React.ReactNode;
  readOnly?: boolean;
  isLoading?: boolean;
  widget?: WidgetWithData;
  title: React.ReactNode;

  onDuplicateWidget: () => void;
  onRemoveWidget: () => void;
  onToggleExpandRows?: (isExpanded: boolean) => void;
}

const WidgetHeader = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      onDuplicateWidget,
      onRemoveWidget,
      onToggleExpandRows,
      widget,
      isLoading,
      options,
      readOnly,
      title,
    },
    ref
  ) => {
    const [isShowingMenu, setIsShowingMenu] = useState(false);
    const [areAllRowsExpanded, setAreAllRowsExpanded] = useState(false);

    const updateExpandedRows = () => {
      const displayedRows = getRowIdsFromWidget(widget);
      const expandedRows = widget?._status?.expandedRowIds ?? [];
      const areAllExpanded = displayedRows.every(id => expandedRows.includes(id));
      setAreAllRowsExpanded(!!areAllExpanded);
    };

    useEffectOnValueChange(widget?.rows, () => updateExpandedRows(), true);
    useEffectOnValueChange(widget?.title, () => updateExpandedRows());
    useEffectOnValueChange(widget?._status?.expandedRowIds, () => updateExpandedRows());

    let titleText: string | undefined;
    if (React.isValidElement<{ widget?: WidgetEntity }>(title)) {
      titleText = title.props.widget?.title;
    } else if (typeof title === 'string') {
      titleText = title;
    }

    const titleTestId = ((titleText?.toLowerCase() as string) ?? 'unknown').replace(/ /g, '-');

    const handleDuplicateWidget = useCallback(() => {
      onDuplicateWidget();
      setIsShowingMenu(false);
    }, [onDuplicateWidget, setIsShowingMenu]);

    const handleRemoveWidget = useCallback(() => {
      onRemoveWidget();
      setIsShowingMenu(false);
    }, [onRemoveWidget, setIsShowingMenu]);

    const handleToggleExpandRows = useCallback(() => {
      onToggleExpandRows?.(areAllRowsExpanded);
    }, [onToggleExpandRows, areAllRowsExpanded]);

    return (
      <div className={styles.widgetHeader} data-testid="widgetheader">
        {!readOnly && (
          <div className={styles.dragHandle} ref={ref}>
            <WidgetDragHandleIcon />
          </div>
        )}
        <div className={styles.title} data-testid={`${titleTestId}-title-widgetheader`}>
          {title}
        </div>

        {onToggleExpandRows && !isLoading && (
          <Button onClick={handleToggleExpandRows} className={styles.toggleExpandButton}>
            <DownDoubleChevron
              className={classNames(styles.toggleExpandArrow, {
                [styles.isExpanded]: areAllRowsExpanded,
              })}
            />
            <span data-testid={`toggle-${areAllRowsExpanded ? 'collapse' : 'expand'}`}>
              {areAllRowsExpanded ? 'Collapse' : 'Expand'} all
            </span>
          </Button>
        )}

        {!readOnly && (
          <Popover
            isOpen={isShowingMenu}
            onOpen={() => setIsShowingMenu(true)}
            onClose={() => setIsShowingMenu(false)}
            position="bottom-end"
            customScrollbars
            maxHeight="100vh"
            target={
              <button
                data-testid="widget-header-settings-button"
                className={classNames(styles.optionButton, { [styles.isActive]: isShowingMenu })}
                disabled={readOnly}
              >
                <Button>
                  <Cog />
                </Button>
              </button>
            }
          >
            <DropdownList minWidth={220}>
              {options && (
                <>
                  {options}
                  <DropdownListItem type="divider" />
                </>
              )}
              <DropdownListItem type="header">Widget</DropdownListItem>
              <DropdownListItem onClick={handleDuplicateWidget}>
                <FontAwesome name="copy" className={dropdownStyles.faIcon} />
                Duplicate
              </DropdownListItem>
              <DropdownListItem type="danger" onClick={handleRemoveWidget}>
                <FontAwesome name="trash" className={dropdownStyles.faIcon} />
                Remove
              </DropdownListItem>
            </DropdownList>
          </Popover>
        )}
      </div>
    );
  }
);

WidgetHeader.displayName = 'WidgetHeader';

export default WidgetHeader;
